/**
 * F-298 / T-240-003 — LinkedIn Texas organic engine FE service.
 *
 * Consumes the canonical LP Texas organic engine (mounted at /api/linkedin
 * in src/routes/linkedin.js, engine in src/lib/linkedin-scheduler.js):
 *   GET  /api/linkedin/texas/post?date=YYYY-MM-DD  -> { success, data } | 404 { success:false }
 *   POST /api/linkedin/texas/post                  -> { date, draft, context, generateDraft } (admin, idempotent per date)
 *   POST /api/linkedin/texas/review                -> { date, action, reviewer, reason, note, content }  (human gate)
 *   POST /api/linkedin/texas/engage                -> { rotationKey, metrics:{views,clicks,conversions} }
 *   GET  /api/linkedin/texas/performance           -> { success, data: rows[] }
 *   GET  /api/linkedin/texas/meta                  -> { success, data: { rotation, statuses, postsPerDay } }
 *
 * There is deliberately NO per-post /status route: the review gate is
 * date + action (approve | publish | reject) against the engine lifecycle
 * DRAFT -> PENDING_REVIEW -> APPROVED -> PUBLISHED (REJECTED).
 */
import { landlordApi } from './api';

export type LinkedInReviewAction = 'approve' | 'publish' | 'reject';

export type LinkedInPostStatus = 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'PUBLISHED' | 'REJECTED' | string;

export interface LinkedInMetrics {
    views?: number;
    clicks?: number;
    conversions?: number;
}

export interface LinkedInTexasPost {
    id: string;
    date: string;
    rotationKey?: string;
    templateName?: string;
    status: LinkedInPostStatus;
    content: string;
    generatedBy?: string;
    review?: {
        reviewer?: string;
        at?: string;
        decision?: string;
        note?: string;
        reason?: string;
        publishedBy?: string;
    } | null;
    createdAt?: string;
    updatedAt?: string;
    publishedAt?: string;
    [key: string]: unknown;
}

export interface LinkedInPerformanceRow {
    rotationKey: string;
    name?: string;
    posts: number;
    views: number;
    clicks: number;
    conversions: number;
    engagementRate: number;
    conversionRate: number;
    [key: string]: unknown;
}

export interface LinkedInTexasMeta {
    rotation?: Array<{ key: string; name?: string; painPoint?: string; valueProp?: string }>;
    statuses?: Record<string, string> | string[];
    postsPerDay?: number;
    [key: string]: unknown;
}

// Engine lifecycle (lib/linkedin-scheduler.js LINKEDIN_POST_STATUS): a fresh
// daily post lands in PENDING_REVIEW; the human gate approves it, then a
// second human action publishes it.

/** Actions the human review gate currently offers for this status. */
export function pendingActions(status?: LinkedInPostStatus): LinkedInReviewAction[] {
    if (status === 'PENDING_REVIEW') return ['approve', 'reject'];
    if (status === 'APPROVED') return ['publish'];
    return [];
}

export function isReviewable(status?: LinkedInPostStatus): boolean {
    const s = status && String(status).toUpperCase();
    return s === 'PENDING_REVIEW' || s === 'APPROVED';
}

export function statusBadgeClass(status?: LinkedInPostStatus): string {
    switch (status && String(status).toUpperCase()) {
        case 'PUBLISHED':
            return 'bg-emerald-100 text-emerald-700';
        case 'APPROVED':
            return 'bg-blue-100 text-blue-700';
        case 'PENDING_REVIEW':
            return 'bg-amber-100 text-amber-700';
        case 'REJECTED':
            return 'bg-red-100 text-red-700';
        default:
            return 'bg-gray-100 text-gray-600';
    }
}

const unwrap = (res: any, label: string): any => {
    const body = res?.data;
    if (body && body.success === false) {
        throw new Error(body.message || `${label} failed`);
    }
    return body && body.data !== undefined ? body.data : body;
};

// GET /texas/post 404s with success:false when no post exists for the date —
// the FE treats that as "nothing generated yet", not an error.
const unwrapOrNull = (res: any, label: string): any => {
    const body = res?.data;
    if (body && body.success === false) return null;
    return body && body.data !== undefined ? body.data : body;
};

const unwrapArray = (res: any, label: string): any[] => {
    const data = unwrap(res, label);
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.rows)) return data.rows;
    if (data && Array.isArray(data.results)) return data.results;
    if (data && Array.isArray(data.items)) return data.items;
    return data ? [data] : [];
};

export const linkedinOrganicApi = {
    getPost: (date?: string): Promise<LinkedInTexasPost | null> =>
        landlordApi
            .get('api/linkedin/texas/post', { params: date ? { date } : undefined })
            .then((r: any) => unwrapOrNull(r, 'linkedin texas post')),

    createPost: (p: { date?: string; draft?: string; context?: string; generateDraft?: boolean }): Promise<LinkedInTexasPost> =>
        landlordApi.post('api/linkedin/texas/post', p).then((r: any) => unwrap(r, 'create linkedin texas post')),

    /** Human review gate: approve | publish | reject the daily post by date. */
    review: (
        date: string,
        r: { action: LinkedInReviewAction; reviewer?: string; reason?: string; note?: string; content?: string },
    ): Promise<LinkedInTexasPost> =>
        landlordApi.post('api/linkedin/texas/review', { date, ...r }).then((res: any) => unwrap(res, 'linkedin texas review')),

    trackEngagement: (rotationKey: string, metrics: LinkedInMetrics): Promise<LinkedInMetrics> =>
        landlordApi
            .post('api/linkedin/texas/engage', { rotationKey, metrics })
            .then((r: any) => unwrap(r, 'linkedin texas engagement')),

    getPerformance: (): Promise<LinkedInPerformanceRow[]> =>
        landlordApi.get('api/linkedin/texas/performance').then((r: any) => unwrapArray(r, 'linkedin texas performance')),

    getMeta: (): Promise<LinkedInTexasMeta> =>
        landlordApi.get('api/linkedin/texas/meta').then((r: any) => unwrap(r, 'linkedin texas meta')),
};

export default linkedinOrganicApi;